import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import type { Browser } from '@/api'
import { useAction, useBrowsers } from '@/api/hooks'
import { copy } from '@/copy'
import { Cell, Heads, SignOutRow } from '@/components/Rows'
import { PageHead } from '@/components/Shell'
import { Failed, Loading } from '@/components/States'
import { useConsole } from '@/lib/store'
import { useTheme, type ThemeChoice } from '@/lib/theme'
import { day, lastUsed } from '@/lib/time'
import { useNow } from '@/lib/useNow'
import { useTitle } from '@/lib/useTitle'

const columns = 'min-[768px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(150px,auto)]'

// Account: the email this console is signed in as, the browsers signed in to
// it (D-052), and the theme. Signing a browser out ends that session only.
export function Account({ account, onSignOut, signingOut }: { account: string; onSignOut: () => void; signingOut: boolean }) {
  useTitle(copy.titles.account)
  const browsers = useBrowsers()
  const now = new Date(useNow(30_000))
  const [theme, setTheme] = useTheme()
  const announce = useConsole((s) => s.announce)
  const signOut = useAction((api, csrf, id: string) => api.signOutBrowser(csrf, id))

  const confirm = (b: Browser) => async () => {
    try {
      await signOut.mutateAsync(b.id)
      announce(copy.account.signedOut(b.name))
      return true
    } catch {
      return false
    }
  }

  const list = browsers.data?.browsers
  let body: ReactNode
  if (!list && browsers.isError) body = <Failed what={copy.account.browsersFailed} onRetry={() => void browsers.refetch()} busy={browsers.isFetching} />
  else if (!list) body = <Loading rows={1} />
  else
    body = (
      <div className="card overflow-hidden">
        <Heads columns={columns} labels={[{ text: copy.account.browser }, { text: copy.devices.signedIn }, { text: copy.devices.lastUsed }]} />
        <ul className="m-0 list-none p-0">
          {list.map((b) =>
            b.current ? (
              <li key={b.id} className="m-0 border-t border-rule px-5 py-4 first:border-t-0 min-[768px]:py-3">
                <div className={`grid grid-cols-1 items-center gap-x-6 gap-y-2 ${columns}`}>
                  <BrowserName browser={b} />
                  <Cell label={copy.devices.signedIn}>{day(new Date(b.signed_in_at), now)}</Cell>
                  <Cell label={copy.devices.lastUsed}>{copy.devices.today}</Cell>
                  <div className="flex min-[768px]:justify-end">
                    <button type="button" className="action w-full min-[768px]:w-auto" onClick={onSignOut} aria-disabled={signingOut || undefined}>
                      {signingOut ? copy.signingOut : copy.signOut}
                    </button>
                  </div>
                </div>
              </li>
            ) : (
              <SignOutRow
                key={b.id}
                name={b.name}
                columns={columns}
                action={copy.signOut}
                ask={copy.account.ask(b.name)}
                failedLine={copy.account.failedAction}
                onConfirm={confirm(b)}
              >
                <BrowserName browser={b} />
                <Cell label={copy.devices.signedIn}>{day(new Date(b.signed_in_at), now)}</Cell>
                <Cell label={copy.devices.lastUsed}>
                  {b.last_used_at ? lastUsed(new Date(b.last_used_at), now, copy.devices.today) : <span className="text-secondary">{copy.devices.never}</span>}
                </Cell>
              </SignOutRow>
            ),
          )}
        </ul>
      </div>
    )

  return (
    <>
      <PageHead title={copy.account.heading} lead={copy.account.lead} />
      <div className="grid max-w-[880px] gap-10">
        <section aria-labelledby="account-email">
          <h2 id="account-email" className="m-0 mb-3 text-[20px] font-[450] leading-7 tracking-[-0.035em]">
            {copy.account.email}
          </h2>
          <p className="m-0 min-w-0 text-[20px] font-medium leading-7 tracking-[-0.01em] [overflow-wrap:anywhere]">{account}</p>
        </section>

        <section aria-labelledby="account-browsers">
          <h2 id="account-browsers" className="m-0 text-[20px] font-[450] leading-7 tracking-[-0.035em]">
            {copy.account.browsers}
          </h2>
          <p className="m-0 mb-3 mt-1 text-secondary">{copy.account.browsersLine}</p>
          {body}
        </section>

        <section aria-labelledby="account-regions">
          <h2 id="account-regions" className="m-0 text-[20px] font-[450] leading-7 tracking-[-0.035em]">
            {copy.regions.accountHeading}
          </h2>
          <p className="m-0 mb-3 mt-1 text-secondary">{copy.regions.accountLine}</p>
          <Link to="/regions" className="action">
            {copy.regions.accountLink}
          </Link>
        </section>

        <section aria-labelledby="account-theme">
          <h2 id="account-theme" className="m-0 text-[20px] font-[450] leading-7 tracking-[-0.035em]">
            {copy.account.theme}
          </h2>
          <p className="m-0 mb-3 mt-1 text-secondary">{copy.account.themeLine}</p>
          <div className="segmented" role="group" aria-labelledby="account-theme">
            {(['dark', 'light', 'system'] as ThemeChoice[]).map((t) => (
              <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}>
                {copy.account.themes[t]}
              </button>
            ))}
          </div>
        </section>
      </div>
    </>
  )
}

function BrowserName({ browser }: { browser: Browser }) {
  return (
    <h3 className="m-0 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[17px] font-medium leading-6">
      <span className="[overflow-wrap:anywhere]">{browser.name}</span>
      {browser.current && (
        <span className="rounded-[4px] border border-thread/40 bg-thread/10 px-2 text-[13px] font-medium leading-6 text-accent">{copy.account.thisBrowser}</span>
      )}
    </h3>
  )
}
